/**
 * 절차적 애니메이션 클립 — idle · walk · run · jump · attack×3 · skill · cast · block · dodge · hit · death (+tpose).
 *
 * 모션 캡처 없이 뼈 회전 키프레임을 코드로 짠다. 게임에 넣을 때 필요한 최소 세트이고,
 * 전부 glb 의 AnimationClip 으로 나가므로 Unity/Godot/Unreal 이 그대로 재생한다.
 *
 * 규약: 회전은 뼈 로컬 X축(앞뒤 스윙)과 Z축(좌우 벌림) 기준. 캐릭터는 +z 를 본다.
 * 다리는 X축 양수가 앞으로 차는 방향이 되도록 부호를 잡았다 — 정면(+z) 을 보는
 * 캐릭터에서 허벅지가 앞으로 나가려면 X축 기준 음의 회전이 필요하다.
 */

import * as THREE from 'three';
import type { BoneName, JointLayout } from './skeleton.ts';

type Bones = Record<BoneName, THREE.Bone>;

const deg = THREE.MathUtils.degToRad;

/** 시간 배열과 "각도 함수"로 쿼터니언 트랙을 만든다. */
function rotTrack(
  bone: THREE.Bone,
  duration: number,
  steps: number,
  fn: (phase: number) => { x?: number; y?: number; z?: number },
): THREE.QuaternionKeyframeTrack {
  const times: number[] = [];
  const values: number[] = [];
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  for (let i = 0; i <= steps; i++) {
    const phase = i / steps;
    const r = fn(phase);
    e.set(deg(r.x ?? 0), deg(r.y ?? 0), deg(r.z ?? 0), 'XYZ');
    q.setFromEuler(e);
    times.push(phase * duration);
    values.push(q.x, q.y, q.z, q.w);
  }
  return new THREE.QuaternionKeyframeTrack(`${bone.name}.quaternion`, times, values);
}

function posTrack(
  bone: THREE.Bone,
  duration: number,
  steps: number,
  fn: (phase: number) => THREE.Vector3,
): THREE.VectorKeyframeTrack {
  const times: number[] = [];
  const values: number[] = [];
  const base = bone.position.clone();
  for (let i = 0; i <= steps; i++) {
    const phase = i / steps;
    const off = fn(phase);
    times.push(phase * duration);
    values.push(base.x + off.x, base.y + off.y, base.z + off.z);
  }
  return new THREE.VectorKeyframeTrack(`${bone.name}.position`, times, values);
}

const S = (phase: number) => Math.sin(phase * Math.PI * 2);
const C = (phase: number) => Math.cos(phase * Math.PI * 2);

export function buildClips(bones: Bones, height: number, layout?: JointLayout): THREE.AnimationClip[] {
  const clips: THREE.AnimationClip[] = [];

  // tpose — Meshy 의 "T-포즈" 출력에 해당. A포즈 위팔을 수평까지 올린 정지 포즈.
  // 뼈 로컬축 = 월드축(뼈에 회전이 없다)이라 위팔 벡터의 각도만큼 z 로 되돌리면 수평이 된다.
  // 팔을 실루엣에서 못 찾았으면(비율 기본값 뼈대) T-포즈는 몸통을 뒤틀 뿐이라 만들지 않는다(실측: 기사).
  if (layout?.armsFound) {
    // 위팔 벡터를 목표 방향(왼팔 +x, 오른팔 −x)까지 돌리는 z 회전. (−180,180] 로 접고 20~80° 로 제한.
    const wrap = (d: number) => ((d + 540) % 360) - 180;
    const lift = (shoulder: THREE.Vector3, elbow: THREE.Vector3, targetDeg: number) => {
      const dx = elbow.x - shoulder.x, dy = elbow.y - shoulder.y;
      return wrap(targetDeg - THREE.MathUtils.radToDeg(Math.atan2(dy, dx)));
    };
    // 실루엣에서 잰 각도는 팔이 몸통과 붙은 캐릭터에서 튄다(로봇: 머리 위까지). 잰 값은 45° 근처로만 보정.
    const zl = Math.min(55, Math.max(30, lift(layout.shoulderL, layout.elbowL, 0)));
    const zr = Math.max(-55, Math.min(-30, lift(layout.shoulderR, layout.elbowR, 180)));
    clips.push(new THREE.AnimationClip('tpose', 0.1, [
      rotTrack(bones.upperArmL, 0.1, 1, () => ({ z: zl })),
      rotTrack(bones.upperArmR, 0.1, 1, () => ({ z: zr })),
    ]));
  }
  const bob = height * 0.012;

  // idle — 숨쉬기. 가슴이 살짝 부풀고 팔이 미세하게 흔들린다.
  clips.push(new THREE.AnimationClip('idle', 2.4, [
    posTrack(bones.hips, 2.4, 24, (p) => new THREE.Vector3(0, bob * 0.5 * S(p), 0)),
    rotTrack(bones.chest, 2.4, 24, (p) => ({ x: -1.5 + 1.5 * S(p) })),
    rotTrack(bones.head, 2.4, 24, (p) => ({ x: 1.2 * S(p + 0.25), y: 2 * S(p * 0.5) })),
    rotTrack(bones.upperArmL, 2.4, 24, (p) => ({ z: -2 + 1.5 * S(p) })),
    rotTrack(bones.upperArmR, 2.4, 24, (p) => ({ z: 2 - 1.5 * S(p) })),
  ]));

  // walk — 다리 교차 스윙, 팔 반대 스윙, 골반 상하.
  const walk = 1.0;
  clips.push(new THREE.AnimationClip('walk', walk, [
    posTrack(bones.hips, walk, 24, (p) => new THREE.Vector3(0, bob * Math.abs(S(p * 2)) * 0.8, 0)),
    rotTrack(bones.thighL, walk, 24, (p) => ({ x: -28 * S(p) })),
    rotTrack(bones.thighR, walk, 24, (p) => ({ x: 28 * S(p) })),
    rotTrack(bones.shinL, walk, 24, (p) => ({ x: 22 * Math.max(0, S(p + 0.25)) + 8 })),
    rotTrack(bones.shinR, walk, 24, (p) => ({ x: 22 * Math.max(0, -S(p + 0.25)) + 8 })),
    rotTrack(bones.upperArmL, walk, 24, (p) => ({ x: 22 * S(p), z: -6 })),
    rotTrack(bones.upperArmR, walk, 24, (p) => ({ x: -22 * S(p), z: 6 })),
    rotTrack(bones.forearmL, walk, 24, (p) => ({ x: -12 - 8 * Math.max(0, S(p)) })),
    rotTrack(bones.forearmR, walk, 24, (p) => ({ x: -12 - 8 * Math.max(0, -S(p)) })),
    rotTrack(bones.chest, walk, 24, (p) => ({ y: 4 * S(p), x: -3 })),
    rotTrack(bones.head, walk, 24, (p) => ({ y: -3 * S(p) })),
  ]));

  // run — 걷기의 강한 버전. 몸을 앞으로 기울인다.
  const run = 0.62;
  clips.push(new THREE.AnimationClip('run', run, [
    posTrack(bones.hips, run, 24, (p) => new THREE.Vector3(0, bob * 2 * Math.abs(S(p * 2)), 0)),
    rotTrack(bones.thighL, run, 24, (p) => ({ x: -45 * S(p) - 5 })),
    rotTrack(bones.thighR, run, 24, (p) => ({ x: 45 * S(p) - 5 })),
    rotTrack(bones.shinL, run, 24, (p) => ({ x: 50 * Math.max(0, S(p + 0.25)) + 10 })),
    rotTrack(bones.shinR, run, 24, (p) => ({ x: 50 * Math.max(0, -S(p + 0.25)) + 10 })),
    rotTrack(bones.upperArmL, run, 24, (p) => ({ x: 40 * S(p), z: -10 })),
    rotTrack(bones.upperArmR, run, 24, (p) => ({ x: -40 * S(p), z: 10 })),
    rotTrack(bones.forearmL, run, 24, () => ({ x: -70 })),
    rotTrack(bones.forearmR, run, 24, () => ({ x: -70 })),
    rotTrack(bones.spine, run, 24, () => ({ x: -12 })),
    rotTrack(bones.chest, run, 24, (p) => ({ y: 8 * S(p) })),
  ]));

  // attack — 오른팔을 들어 내려친다. 예비 동작 → 타격 → 복귀.
  const atk = 0.7;
  const swing = (p: number) => (p < 0.35 ? -90 * (p / 0.35) : p < 0.55 ? -90 + 150 * ((p - 0.35) / 0.2) : 60 * (1 - (p - 0.55) / 0.45));
  clips.push(new THREE.AnimationClip('attack', atk, [
    rotTrack(bones.upperArmR, atk, 28, (p) => ({ x: swing(p), z: 20 })),
    rotTrack(bones.forearmR, atk, 28, (p) => ({ x: p < 0.35 ? -60 * (p / 0.35) : -60 * Math.max(0, 1 - (p - 0.35) / 0.3) })),
    rotTrack(bones.chest, atk, 28, (p) => ({ y: p < 0.35 ? 18 * (p / 0.35) : p < 0.55 ? 18 - 40 * ((p - 0.35) / 0.2) : -22 * (1 - (p - 0.55) / 0.45) })),
    rotTrack(bones.hips, atk, 28, (p) => ({ y: p < 0.35 ? 8 * (p / 0.35) : p < 0.55 ? 8 - 18 * ((p - 0.35) / 0.2) : -10 * (1 - (p - 0.55) / 0.45) })),
    rotTrack(bones.thighL, atk, 28, (p) => ({ x: -10 * Math.sin(Math.PI * p) })),
    rotTrack(bones.upperArmL, atk, 28, (p) => ({ x: 15 * Math.sin(Math.PI * p), z: -10 })),
  ]));

  // hit — 뒤로 움찔. 가슴·머리가 젖혀졌다 돌아온다.
  const hit = 0.45;
  const flinch = (p: number) => Math.sin(Math.PI * Math.min(1, p * 1.6)) * Math.max(0, 1 - p * 0.4);
  clips.push(new THREE.AnimationClip('hit', hit, [
    rotTrack(bones.spine, hit, 18, (p) => ({ x: 14 * flinch(p) })),
    rotTrack(bones.chest, hit, 18, (p) => ({ x: 10 * flinch(p), y: 6 * flinch(p) })),
    rotTrack(bones.head, hit, 18, (p) => ({ x: 18 * flinch(p) })),
    rotTrack(bones.upperArmL, hit, 18, (p) => ({ x: -25 * flinch(p), z: -12 * flinch(p) })),
    rotTrack(bones.upperArmR, hit, 18, (p) => ({ x: -25 * flinch(p), z: 12 * flinch(p) })),
    posTrack(bones.hips, hit, 18, (p) => new THREE.Vector3(0, 0, -height * 0.04 * flinch(p))),
  ]));

  // ── 게임 표준 세트 확장 (2026-09-04) ─────────────────────────────────────
  // 이징: 0→1 을 부드럽게. 구간 함수 seg(p, a, b) 는 p 가 [a,b] 안에서 0→1.
  const ease = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));
  const seg = (p: number, a: number, b: number) => ease((p - a) / (b - a));
  const pulse = (p: number, a: number, b: number) => Math.sin(Math.PI * Math.min(1, Math.max(0, (p - a) / (b - a))));
  const v3 = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

  // jump — 예비 웅크림(0~0.25) → 도약(0.25~0.65, 정점 0.45) → 착지 웅크림(0.65~0.85) → 복귀.
  const jmp = 0.9;
  const crouch = (p: number) => pulse(p, 0, 0.3) * 0.7 + pulse(p, 0.62, 0.92);
  const air = (p: number) => pulse(p, 0.25, 0.7);
  clips.push(new THREE.AnimationClip('jump', jmp, [
    posTrack(bones.hips, jmp, 36, (p) => v3(0, -height * 0.06 * crouch(p) + height * 0.32 * air(p), 0)),
    rotTrack(bones.thighL, jmp, 36, (p) => ({ x: -35 * crouch(p) + 25 * air(p) })),
    rotTrack(bones.thighR, jmp, 36, (p) => ({ x: -35 * crouch(p) + 25 * air(p) })),
    rotTrack(bones.shinL, jmp, 36, (p) => ({ x: 55 * crouch(p) + 40 * air(p) })),
    rotTrack(bones.shinR, jmp, 36, (p) => ({ x: 55 * crouch(p) + 40 * air(p) })),
    rotTrack(bones.spine, jmp, 36, (p) => ({ x: -18 * crouch(p) + 6 * air(p) })),
    rotTrack(bones.upperArmL, jmp, 36, (p) => ({ x: 30 * crouch(p) - 60 * air(p), z: -8 - 25 * air(p) })),
    rotTrack(bones.upperArmR, jmp, 36, (p) => ({ x: 30 * crouch(p) - 60 * air(p), z: 8 + 25 * air(p) })),
    rotTrack(bones.forearmL, jmp, 36, (p) => ({ x: -30 * crouch(p) - 20 * air(p) })),
    rotTrack(bones.forearmR, jmp, 36, (p) => ({ x: -30 * crouch(p) - 20 * air(p) })),
    rotTrack(bones.head, jmp, 36, (p) => ({ x: 8 * crouch(p) - 10 * air(p) })),
  ]));

  // attack2 — 콤보 2타. 오른팔 수평 베기(오른쪽→왼쪽), 몸통을 크게 비튼다.
  const a2 = 0.6;
  const sweep = (p: number) => (p < 0.3 ? -seg(p, 0, 0.3) : p < 0.55 ? -1 + 2 * seg(p, 0.3, 0.55) : 1 - seg(p, 0.55, 1)); // -1 예비 … +1 끝
  clips.push(new THREE.AnimationClip('attack2', a2, [
    rotTrack(bones.upperArmR, a2, 28, (p) => ({ x: -70 - 10 * sweep(p), y: 55 * sweep(p), z: 30 })),
    rotTrack(bones.forearmR, a2, 28, (p) => ({ x: -35 + 25 * Math.abs(sweep(p)) })),
    rotTrack(bones.chest, a2, 28, (p) => ({ y: -30 * sweep(p), x: -6 })),
    rotTrack(bones.spine, a2, 28, (p) => ({ y: -12 * sweep(p) })),
    rotTrack(bones.hips, a2, 28, (p) => ({ y: -10 * sweep(p) })),
    rotTrack(bones.upperArmL, a2, 28, (p) => ({ x: 20 * sweep(p), z: -18 })),
    rotTrack(bones.thighR, a2, 28, (p) => ({ x: -12 * pulse(p, 0.25, 0.7) })),
    rotTrack(bones.head, a2, 28, (p) => ({ y: 10 * sweep(p) })),
  ]));

  // attack3 — 콤보 3타 피니시. 두 손 머리 위로 들어(예비) 앞으로 뛰며 내려찍는다.
  const a3 = 0.85;
  const wind = (p: number) => seg(p, 0, 0.38) * (1 - seg(p, 0.38, 0.52));
  const slam = (p: number) => seg(p, 0.38, 0.52) * (1 - seg(p, 0.72, 1));
  clips.push(new THREE.AnimationClip('attack3', a3, [
    posTrack(bones.hips, a3, 34, (p) => v3(0, height * 0.14 * pulse(p, 0.3, 0.55) - height * 0.07 * pulse(p, 0.5, 0.85), height * 0.06 * seg(p, 0.35, 0.55))),
    rotTrack(bones.upperArmL, a3, 34, (p) => ({ x: -160 * wind(p) - 20 * slam(p), z: -20 })),
    rotTrack(bones.upperArmR, a3, 34, (p) => ({ x: -160 * wind(p) - 20 * slam(p), z: 20 })),
    rotTrack(bones.forearmL, a3, 34, (p) => ({ x: -40 * wind(p) })),
    rotTrack(bones.forearmR, a3, 34, (p) => ({ x: -40 * wind(p) })),
    rotTrack(bones.spine, a3, 34, (p) => ({ x: 14 * wind(p) - 35 * slam(p) })),
    rotTrack(bones.chest, a3, 34, (p) => ({ x: 6 * wind(p) - 15 * slam(p) })),
    rotTrack(bones.thighL, a3, 34, (p) => ({ x: -50 * slam(p) })),
    rotTrack(bones.thighR, a3, 34, (p) => ({ x: -15 * slam(p) })),
    rotTrack(bones.shinL, a3, 34, (p) => ({ x: 70 * slam(p) })),
    rotTrack(bones.shinR, a3, 34, (p) => ({ x: 25 * slam(p) })),
    rotTrack(bones.head, a3, 34, (p) => ({ x: -10 * wind(p) + 12 * slam(p) })),
  ]));

  // dodge — 뒤로 짧게 구르듯 물러선다. 웅크리며 뒤로, 팔은 앞으로 균형.
  const dg = 0.5;
  const back = (p: number) => seg(p, 0, 0.45);
  const tuck = (p: number) => pulse(p, 0.05, 0.75);
  clips.push(new THREE.AnimationClip('dodge', dg, [
    posTrack(bones.hips, dg, 24, (p) => v3(0, -height * 0.1 * tuck(p) + height * 0.05 * pulse(p, 0.1, 0.5), -height * 0.22 * back(p) + height * 0.22 * seg(p, 0.6, 1) )),
    rotTrack(bones.spine, dg, 24, (p) => ({ x: -35 * tuck(p) })),
    rotTrack(bones.chest, dg, 24, (p) => ({ x: -15 * tuck(p) })),
    rotTrack(bones.head, dg, 24, (p) => ({ x: 20 * tuck(p) })),
    rotTrack(bones.thighL, dg, 24, (p) => ({ x: -55 * tuck(p) })),
    rotTrack(bones.thighR, dg, 24, (p) => ({ x: -40 * tuck(p) })),
    rotTrack(bones.shinL, dg, 24, (p) => ({ x: 80 * tuck(p) })),
    rotTrack(bones.shinR, dg, 24, (p) => ({ x: 65 * tuck(p) })),
    rotTrack(bones.upperArmL, dg, 24, (p) => ({ x: -50 * tuck(p), z: -20 })),
    rotTrack(bones.upperArmR, dg, 24, (p) => ({ x: -50 * tuck(p), z: 20 })),
    rotTrack(bones.forearmL, dg, 24, (p) => ({ x: -40 * tuck(p) })),
    rotTrack(bones.forearmR, dg, 24, (p) => ({ x: -40 * tuck(p) })),
  ]));

  // block — 왼팔을 얼굴 앞에 들어 막는 자세. 올라가서(0~0.25) 버티고(살짝 흔들림) 내린다(0.8~1).
  const bk = 1.0;
  const hold = (p: number) => seg(p, 0, 0.25) * (1 - seg(p, 0.8, 1));
  clips.push(new THREE.AnimationClip('block', bk, [
    rotTrack(bones.upperArmL, bk, 30, (p) => ({ x: -75 * hold(p), y: 25 * hold(p), z: -15 * hold(p) })),
    rotTrack(bones.forearmL, bk, 30, (p) => ({ x: -110 * hold(p), z: -30 * hold(p) })),
    rotTrack(bones.upperArmR, bk, 30, (p) => ({ x: -20 * hold(p), z: 25 * hold(p) })),
    rotTrack(bones.forearmR, bk, 30, (p) => ({ x: -60 * hold(p) })),
    rotTrack(bones.chest, bk, 30, (p) => ({ y: 18 * hold(p), x: -8 * hold(p) + 1.5 * S(p * 3) * hold(p) })),
    rotTrack(bones.hips, bk, 30, (p) => ({ y: 12 * hold(p) })),
    rotTrack(bones.thighL, bk, 30, (p) => ({ x: -18 * hold(p) })),
    rotTrack(bones.shinL, bk, 30, (p) => ({ x: 22 * hold(p) })),
    rotTrack(bones.shinR, bk, 30, (p) => ({ x: 10 * hold(p) })),
    rotTrack(bones.head, bk, 30, (p) => ({ x: 10 * hold(p), y: -8 * hold(p) })),
    posTrack(bones.hips, bk, 30, (p) => v3(0, -height * 0.03 * hold(p), 0)),
  ]));

  // cast — 두 손을 가슴 앞에 모았다가(차지 0~0.45, 떨림) 앞으로 밀어낸다(발사 0.45~0.6) → 복귀.
  const cs = 1.2;
  const charge = (p: number) => seg(p, 0, 0.2) * (1 - seg(p, 0.42, 0.55));
  const release = (p: number) => seg(p, 0.42, 0.55) * (1 - seg(p, 0.8, 1));
  const tremor = (p: number) => 2.5 * Math.sin(p * Math.PI * 22) * charge(p) * seg(p, 0.2, 0.42);
  clips.push(new THREE.AnimationClip('cast', cs, [
    rotTrack(bones.upperArmL, cs, 40, (p) => ({ x: -55 * charge(p) - 95 * release(p) + tremor(p), y: 35 * charge(p), z: -10 })),
    rotTrack(bones.upperArmR, cs, 40, (p) => ({ x: -55 * charge(p) - 95 * release(p) - tremor(p), y: -35 * charge(p), z: 10 })),
    rotTrack(bones.forearmL, cs, 40, (p) => ({ x: -100 * charge(p) - 10 * release(p), y: -20 * charge(p) })),
    rotTrack(bones.forearmR, cs, 40, (p) => ({ x: -100 * charge(p) - 10 * release(p), y: 20 * charge(p) })),
    rotTrack(bones.spine, cs, 40, (p) => ({ x: 8 * charge(p) - 14 * release(p) })),
    rotTrack(bones.chest, cs, 40, (p) => ({ x: 6 * charge(p) - 8 * release(p) })),
    rotTrack(bones.head, cs, 40, (p) => ({ x: 12 * charge(p) - 6 * release(p) })),
    rotTrack(bones.thighR, cs, 40, (p) => ({ x: 6 * charge(p) - 22 * release(p) })),
    rotTrack(bones.thighL, cs, 40, (p) => ({ x: 10 * release(p) })),
    rotTrack(bones.shinR, cs, 40, (p) => ({ x: 18 * release(p) })),
    posTrack(bones.hips, cs, 40, (p) => v3(0, -height * 0.02 * charge(p), height * 0.05 * release(p))),
  ]));

  // skill — 회전 베기. 웅크렸다가 한 바퀴(0.15~0.75) 돌며 양팔을 벌린다.
  const sk = 0.95;
  const spinT = (p: number) => seg(p, 0.15, 0.75);
  const arms = (p: number) => pulse(p, 0.05, 0.9);
  clips.push(new THREE.AnimationClip('skill', sk, [
    rotTrack(bones.hips, sk, 48, (p) => ({ y: 360 * spinT(p) })),
    posTrack(bones.hips, sk, 48, (p) => v3(0, -height * 0.05 * pulse(p, 0, 0.3) + height * 0.08 * pulse(p, 0.2, 0.75), 0)),
    rotTrack(bones.upperArmL, sk, 48, (p) => ({ z: -80 * arms(p), x: -15 * arms(p) })),
    rotTrack(bones.upperArmR, sk, 48, (p) => ({ z: 80 * arms(p), x: -15 * arms(p) })),
    rotTrack(bones.forearmL, sk, 48, (p) => ({ x: -15 * arms(p) })),
    rotTrack(bones.forearmR, sk, 48, (p) => ({ x: -15 * arms(p) })),
    rotTrack(bones.spine, sk, 48, (p) => ({ x: -10 * arms(p) })),
    rotTrack(bones.chest, sk, 48, (p) => ({ y: 25 * Math.sin(Math.PI * 2 * spinT(p)) })),
    rotTrack(bones.thighL, sk, 48, (p) => ({ x: -20 * pulse(p, 0, 0.3), z: 12 * arms(p) })),
    rotTrack(bones.thighR, sk, 48, (p) => ({ x: -20 * pulse(p, 0, 0.3), z: -12 * arms(p) })),
    rotTrack(bones.shinL, sk, 48, (p) => ({ x: 30 * pulse(p, 0, 0.3) })),
    rotTrack(bones.shinR, sk, 48, (p) => ({ x: 30 * pulse(p, 0, 0.3) })),
  ]));

  // death — 뒤로 무너진다. 무릎이 꺾이고(0~0.4) 골반이 내려앉으며 상체가 눕는다(0.3~0.85). 마지막 프레임에서 멈춘다.
  const de = 1.4;
  const knee = (p: number) => seg(p, 0, 0.4);
  const fall = (p: number) => seg(p, 0.3, 0.85);
  const hipY = bones.hips.position.y;
  const legR = Math.max(height * 0.05, (layout?.radii.leg ?? height * 0.06));
  clips.push(new THREE.AnimationClip('death', de, [
    posTrack(bones.hips, de, 48, (p) => v3(0, -(hipY * 0.45) * knee(p) - Math.max(0, hipY * 0.55 - legR) * fall(p), -height * 0.18 * fall(p))),
    rotTrack(bones.hips, de, 48, (p) => ({ x: 80 * fall(p) })),
    rotTrack(bones.spine, de, 48, (p) => ({ x: 10 * knee(p) - 5 * fall(p) })),
    rotTrack(bones.chest, de, 48, (p) => ({ x: 8 * knee(p) })),
    rotTrack(bones.head, de, 48, (p) => ({ x: 25 * knee(p) - 5 * fall(p), y: 15 * fall(p) })),
    rotTrack(bones.thighL, de, 48, (p) => ({ x: -45 * knee(p) + 40 * fall(p), z: 8 * fall(p) })),
    rotTrack(bones.thighR, de, 48, (p) => ({ x: -55 * knee(p) + 45 * fall(p), z: -10 * fall(p) })),
    rotTrack(bones.shinL, de, 48, (p) => ({ x: 90 * knee(p) - 60 * fall(p) })),
    rotTrack(bones.shinR, de, 48, (p) => ({ x: 100 * knee(p) - 55 * fall(p) })),
    rotTrack(bones.upperArmL, de, 48, (p) => ({ x: -35 * knee(p) + 20 * fall(p), z: -25 * knee(p) - 30 * fall(p) })),
    rotTrack(bones.upperArmR, de, 48, (p) => ({ x: -35 * knee(p) + 20 * fall(p), z: 25 * knee(p) + 35 * fall(p) })),
    rotTrack(bones.forearmL, de, 48, (p) => ({ x: -30 * knee(p) + 20 * fall(p) })),
    rotTrack(bones.forearmR, de, 48, (p) => ({ x: -30 * knee(p) + 20 * fall(p) })),
  ]));

  return clips;
}

/** 게임 쪽에서 상태기계를 짤 때 쓰는 메타 — 루프 여부와 "마지막 프레임에서 멈춤". */
export const CLIP_META: Record<string, { loop: boolean; clamp?: boolean; group: 'locomotion' | 'combat' | 'react' | 'pose' }> = {
  tpose: { loop: false, clamp: true, group: 'pose' },
  idle: { loop: true, group: 'locomotion' },
  walk: { loop: true, group: 'locomotion' },
  run: { loop: true, group: 'locomotion' },
  jump: { loop: false, group: 'locomotion' },
  attack: { loop: false, group: 'combat' },
  attack2: { loop: false, group: 'combat' },
  attack3: { loop: false, group: 'combat' },
  skill: { loop: false, group: 'combat' },
  cast: { loop: false, group: 'combat' },
  block: { loop: false, group: 'combat' },
  dodge: { loop: false, group: 'react' },
  hit: { loop: false, group: 'react' },
  death: { loop: false, clamp: true, group: 'react' },
};

export const CLIP_LABELS: Record<string, string> = {
  tpose: 'T-포즈',
  idle: '대기',
  walk: '걷기',
  run: '달리기',
  jump: '점프',
  attack: '공격 1',
  attack2: '공격 2',
  attack3: '공격 3 (피니시)',
  skill: '스킬 (회전베기)',
  cast: '캐스팅',
  block: '방어',
  dodge: '회피',
  hit: '피격',
  death: '사망',
};

